import { PencilSimpleIcon } from '@phosphor-icons/react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useState } from 'react';

import { DatePicker } from '@/components/common/date-picker';
import { TextButton } from '@/components/common/text-button';
import { useToast } from '@/components/feedback/toast';
import { fromDate, toDayjs } from '@/lib/date';

import { formatDetailDate } from '../format';
import { useUpdateExpectedDelivery } from '../hooks';
import type { OrderDetail } from '../types';

import styles from './order-detail.module.scss';

/** Inline Expected Delivery editor — auto-saves on date select; min date = order date. */
export function ExpectedDeliveryField({ order }: { order: OrderDetail }) {
  const [editing, setEditing] = useState(false);
  const update = useUpdateExpectedDelivery(order.id);
  const toast = useToast();
  const orderDate = order.orderMeta.orderDate;
  const value = order.orderMeta.expectedDelivery;

  const save = async (d: Dayjs | null) => {
    setEditing(false);
    const iso = fromDate(d);
    if (!iso) return;
    try {
      await update.mutateAsync(iso);
      toast.success({ title: 'Expected delivery date updated.' });
    } catch (e) {
      toast.error({ title: e instanceof Error ? e.message : 'Could not update the date.' });
    }
  };

  if (editing) {
    return (
      <DatePicker
        open
        autoFocus
        defaultValue={toDayjs(value ?? undefined)}
        disabledDate={(c) => c.isBefore(dayjs(orderDate), 'day')}
        onChange={(d) => void save(d as Dayjs | null)}
        onOpenChange={(o) => {
          if (!o) setEditing(false);
        }}
        format="MMM DD, YYYY"
      />
    );
  }

  if (!value) {
    return (
      <TextButton tone="brand" size="sm" onClick={() => setEditing(true)}>
        Add
      </TextButton>
    );
  }

  return (
    <span className={styles.editableValue}>
      {formatDetailDate(value)}
      <button
        type="button"
        className={styles.editIcon}
        aria-label="Edit expected delivery date"
        onClick={() => setEditing(true)}
      >
        <PencilSimpleIcon size={14} />
      </button>
    </span>
  );
}
